import { ICartData } from '../../../interfaces';
import React, { useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import ProductPriceFormat from '../../../../../products/product-price/product-price-format';
import TermsAndConditions from '../../shared/terms-and-conditions';
import CheckIcon from '../../../../../../ui/svg/common/check-v';
import {
    IFreePluginProgressBarWithMilestones,
    initFreePluginProgressBarWithMilestones
} from '../../../domain/free-plugin-milestones-provider';

interface IFreePluginProgressBarWithMilestonesProps {
    cartData: ICartData;
    classes?: string;
}

const CHECKED_CIRCLE_SIZE = 16;
const UNCHECKED_CIRCLE_SIZE = 13;

/**
 * https://wavesaudio.atlassian.net/browse/DSWEB-5475
 */
const FreePluginProgressBarWithMilestones = ({ cartData, classes }: IFreePluginProgressBarWithMilestonesProps) => {

    const [progressBar, setProgressBar] = useState<IFreePluginProgressBarWithMilestones|null>(null);
    const [enabled, setEnabled] = useState(false);

    useEffect(() => {
        const data = initFreePluginProgressBarWithMilestones(cartData);
        setEnabled(data?.enabled ?? false);

        if(!data.enabled || isEqual(data, progressBar)) return;

        // show progress bar ----
        setProgressBar(data);

    }, [
        cartData,
        progressBar,
    ]);

    if(!cartData || !enabled || !progressBar) return null;

    const missingAmount = progressBar.checkedItem?.missingAmount ?? 0;

    return (
        <div className={ `w-full mt-3 ${ classes ?? '' }` } style={{
            width: 'calc(100% - 5px)'
        }}>

            <div
                role="progressbar"
                aria-labelledby="free-plugin-progress-label"
                aria-valuemin={ progressBar.min }
                aria-valuemax={ progressBar.max }
                aria-valuenow={ progressBar?.checkedItem?.missingAmount ?? 0 }
                className="w-full rounded relative mb-8"
                style={{
                    backgroundColor: '#E5EDEA',
                    overflow: 'visible',
                }}
            >
                <div
                    className="h-full bg-green rounded"
                    style={{
                        width: `${ progressBar?.checkedItem?.percent ?? 0 }%`,
                        height: '7px',
                    }}
                />

                <div className="absolute text-xs top-0 left-0 w-full">
                    {
                        progressBar.milestones?.map(milestone => {

                            const itemSize = milestone.isChecked ? CHECKED_CIRCLE_SIZE : UNCHECKED_CIRCLE_SIZE;

                            return (
                                <div
                                    key={ milestone.amount }
                                    className="absolute flex flex-col items-center"
                                    style={{
                                        left: `${ milestone.percent }%`,
                                        top: milestone.isChecked ? '-4px' : '-3px',
                                        transform: 'translateX(-50%)',
                                    }}>

                                    <div
                                        className="flex items-center justify-center"
                                        style={{
                                            width: `${ itemSize }px`,
                                            height: `${ itemSize }px`,
                                            borderRadius: '100%',
                                            background: milestone.isChecked ? '#00BA90' : '#fff',
                                            border: milestone.isChecked ? '2px solid #fff' : '2px solid #CFD9D4',
                                        }}
                                    >
                                        {
                                            milestone.isChecked &&
                                            <CheckIcon
                                                width={ '10px' }
                                                height={ '10px' }
                                                color={ '#fff' }
                                            />
                                        }
                                    </div>

                                    <div
                                        className="flex font-bold"
                                        style={{
                                            width: 'fit-content',
                                            marginTop: milestone.isChecked ? '5px' : '8px',
                                            marginRight: '1px',
                                            color: milestone.isChecked ? '#111' : '#A2AAA7',
                                        }}>
                                        <ProductPriceFormat price={ milestone.displayAmount } />
                                    </div>
                                </div>
                            )
                        })
                    }
                </div>
            </div>

            {
                missingAmount > 0 &&
                <div className="flex items-center justify-between gap-2 mt-2 md:whitespace-nowrap flex-wrap md:flex-nowrap">
                    <div
                        id="free-plugin-progress-label"
                        className="text-sm">
                        Add <ProductPriceFormat price={ missingAmount } /> to get <strong className="font-bold">{ progressBar.checkedItem?.receiveCount } FREE plugin{ progressBar.checkedItem?.receiveCount === 1 ? '' : 's' }</strong>
                    </div>

                    <TermsAndConditions
                        url={ cartData.efstcurl }
                        classes="md:ml-auto block"
                        styles={{
                            lineHeight: '1',
                            marginTop: '1px',
                        }}
                    />
                </div>
            }

        </div>
    )
};

export default FreePluginProgressBarWithMilestones;